import { Arg, Metadata } from '../../../../utils/spds/metadata';
import {
  TOAST_ERROR_TYPE,
  TOAST_SUCCESS_TYPE,
  TOAST_SUPPORTED_TYPES,
  TOAST_SYSTEM_TYPE,
} from '../../../../utils/spds/constants/feedback';
import { fn } from '@ember/helper';
import { isEmpty } from '@ember/utils';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import CloseButton from '../../action/button/close.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import GhostButton from '../../action/button/ghost.gjs';
import Super from '../../core/base.gjs';
import styles from './message.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const METADATA = Metadata.define({
  description: `
Individual toast notification message. Displays a type-based icon, message text, optional CTA button, and a close button.

The toast data object is passed as the \`@toast\` argument and contains type, message, and optional cta, alignItems, flex, and gap properties.

The \`Spds::Feedback::Toast::Container\` component must be present at the root of the application to handle toast service events. A local container can be rendered inside full screen modals using \`@local={{true}}\`.
  `,
  meta: {
    index: Arg.number({ description: 'Position index of the toast in the container' }),
    justifyContent: Arg.string({
      default: 'space-between',
      description: 'CSS justify-content for the toast layout',
    }),
    onManualDismiss: Arg.action({
      description: 'Action triggered when the toast is manually dismissed',
    }),
    toast: Arg.object({
      description: 'Toast data object containing type, message, and optional cta properties',
    })
      .required()
      .properties({
        cta: Arg.object({ description: 'Optional CTA button configuration' }).properties({
          action: Arg.action({
            description: 'Action to perform when the CTA button is clicked',
          }).required(),
          text: Arg.string({ description: 'CTA button text' }).required(),
        }),
        message: Arg.string({ description: 'Toast message text' }).required(),
        type: Arg.oneOf(TOAST_SUPPORTED_TYPES, {
          description: 'Visual style variant of the toast',
        }).required(),
      }),
  },
  name: 'Toast Message',
  resources: {
    zeroheight: 'https://zeroheight.com/3ddd0f892/p/63c477-toast',
  },
});

export default class SpdsFeedbackToastMessage extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-toast-message';
  classesToMapToAttrs = ['hasCta', 'type'];
  styles = styles;

  get alignItems() {
    return this.toast.alignItems ?? 'center';
  }

  get flex() {
    return this.toast.flex ?? '1 0 auto';
  }

  get gap() {
    return this.toast.gap ?? '16px';
  }

  get hasCta() {
    return !isEmpty(this.toast.cta);
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  get toast() {
    return this.args.toast;
  }

  get type() {
    return this.toast.type;
  }

  get icon() {
    let { type } = this;
    let icon;

    switch (type) {
      case TOAST_ERROR_TYPE:
        icon = 'triangle-exclamation-mark';
        break;
      case TOAST_SUCCESS_TYPE:
        icon = 'circle-check';
        break;
      case TOAST_SYSTEM_TYPE:
        icon = 'circle-i';
        break;
      default:
        icon = null;
    }

    return icon;
  }

  ctaClick = async () => {
    let shouldClose = await Promise.resolve(this.toast.cta.action?.());

    if (shouldClose || isEmpty(shouldClose)) {
      this.args.onManualDismiss?.(this.toast);
    }
  };

  <template>
    <FlexRow
      @alignItems={{this.alignItems}}
      @flex={{this.flex}}
      @gap={{this.gap}}
      @justifyContent={{this.justifyContent}}
      @tag="li"
      aria-live="assertive"
      class={{this.classes}}
      data-test-spds-feedback-toast-message={{or @index "0"}}
      role="status"
      {{this.validateArguments}}
      ...attributes
    >
      {{#if this.icon}}
        {{svgJar this.icon class="icon" data-test-spds-feedback-toast-message-icon=""}}
      {{/if}}
      <FlexRow @flex="1 0 auto" class="text" data-test-spds-feedback-toast-message-text>
        {{@toast.message}}
      </FlexRow>
      {{#if @toast.cta}}
        <GhostButton
          class="cta-btn"
          data-test-spds-feedback-toast-message-cta-button
          type="button"
          {{on "click" this.ctaClick}}
        >
          {{@toast.cta.text}}
        </GhostButton>
      {{/if}}
      <CloseButton
        @close={{fn @onManualDismiss @toast}}
        class="dismissal-btn"
        data-test-spds-feedback-toast-message-close-button
      />
    </FlexRow>
  </template>
}

export const TOAST_MESSAGE_METADATA = SpdsFeedbackToastMessage.metadata;
